import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { schema as schemaA } from './endpoints';
import { schema as schemaB } from './openapi-schema';
import { ApiError } from './error';
import qs from 'qs';

type Merged<T, U> = Omit<T, keyof U> & U;
type Schema = Merged<schemaA, schemaB>;

type InnerReq<P extends keyof Schema, M extends keyof Schema[P]> = Schema[P][M] extends {
    request: { param?: infer RP; query?: infer RQ; body?: infer RB };
}
    ? { param: RP; query: RQ; body: RB }
    : never;
export type Req<P extends keyof Schema, M extends keyof Schema[P]> = (unknown extends InnerReq<P, M>['param']
    ? { param?: never }
    : { param: InnerReq<P, M>['param'] }) &
    (unknown extends InnerReq<P, M>['query'] ? { query?: never } : { query: InnerReq<P, M>['query'] }) &
    (unknown extends InnerReq<P, M>['body'] ? { body?: never } : { body: InnerReq<P, M>['body'] });

export type Res<P extends keyof Schema, M extends keyof Schema[P]> = Schema[P][M] extends { response: { body: infer R } } ? R : never;

export interface ReqOptions {
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
    onDownloadProgress?: (progressEvent: ProgressEvent) => void;
}

export function api<P extends keyof Schema, M extends keyof Schema[P]>(path: P, method: M) {
    return async (request: Req<P, M>, options?: ReqOptions) => {
        const templateParams = path.match(/{([^}]+)}/g);
        let realPath = path as string;
        if (request?.param && templateParams) {
            templateParams?.forEach((templateParam) => {
                const paramName = templateParam.replace(/{|}/g, '');
                const paramValue = (request.param as Record<string, any>)?.[paramName];
                realPath = realPath.replace(templateParam, paramValue);
            });
        }

        // axios config, getting everything as arraybuffer to handle all types of response properly, we will decode it later based on content-type header
        const config: AxiosRequestConfig = {
            url: realPath,
            method: method as string,
            baseURL: import.meta.env.VITE_API_URL,
            responseType: 'arraybuffer',
        };
        if (request?.query) config.params = request.query;
        if (request?.body) {
            nullify(request.body);
            config.data = request.body;
        }
        if (options?.onUploadProgress) config.onUploadProgress = options.onUploadProgress;
        if (options?.onDownloadProgress) config.onDownloadProgress = options.onDownloadProgress;

        // set auth header
        const token = localStorage.getItem('token');
        if (token) {
            if (!config.headers) config.headers = {};
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        const version = import.meta.env.VITE_APP_VERSION;
        if (version) {
            if (!config.headers) config.headers = {};
            config.headers['X-App-Version'] = version;
        }

        const client = axios.create({
            paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
        });

        try {
            const response = await client.request(config);
            setResponseData(response);

            return response.data as Res<P, M>;
        } catch (error: any) {
            if (error.response) {
                setResponseData(error.response);
                const errorMessage = error.response.data?.title || error.response.data?.error || error.response.data?.message;

                switch (error.response.status) {
                    case 400:
                    case 422:
                        const validationErrors: string[] = [];
                        const data = error.response.data;
                        if (data.errors) {
                            if (Array.isArray(data.errors)) {
                                for (const err of data.errors) {
                                    if (err.message) validationErrors.push(err.message);
                                }
                            } else {
                                for (const key in data.errors) {
                                    if (data.errors[key]) {
                                        validationErrors.push(Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]);
                                    }
                                }
                            }
                        } else {
                            for (const k in data) {
                                validationErrors.push(Array.isArray(data[k]) ? data[k][0] : data[k]);
                            }
                        }
                        throw new ApiError(errorMessage || 'Invalid data.', error.response.status, validationErrors);
                    case 401:
                        throw new ApiError(errorMessage || 'Your session is inactive. Please login again.', 401);
                    case 500:
                        throw new ApiError(errorMessage || 'Something went wrong.', 500);
                    case 404:
                        throw new ApiError(errorMessage || 'Resource not found.', 404);
                    case 403:
                        throw new ApiError(errorMessage || 'You do not have proper permission to access this resource.', 403);
                    case 429:
                        throw new ApiError(errorMessage || 'Too many attempts. Please try again after some time.', 429);
                    default:
                        throw new ApiError(errorMessage || error.toString(), error.response.status);
                }
            } else if (error.request) {
                throw new ApiError('Could not connect to server', 0);
            } else {
                throw new ApiError('An unknown error occurred', 0);
            }
        }
    };
}

function setResponseData(response: AxiosResponse) {
    const contentType = response.headers['content-type'];
    const isJson = contentType && (contentType.includes('application/json') || contentType.includes('application/problem+json'));
    const isMsgPack = contentType && contentType.includes('application/x-msgpack');
    const isText = contentType && contentType.includes('text/plain');
    if (isJson) {
        response.data = JSON.parse(new TextDecoder().decode(response.data));
        handleDates(response.data);
    } else if (isMsgPack) {
        // response.data = decode(response.data, { useBigInt64: true });
        // handleDates(response.data);
    } else if (isText) {
        response.data = new TextDecoder().decode(response.data);
    } else {
        const contentDisposition = response.headers['content-disposition'];
        const filename = getFilename(contentDisposition) || 'file';
        response.data = {
            name: filename,
            data: new Blob([response.data], { type: contentType }),
        };
    }
}

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

/**
 * Update response body with parsed dates. It recursively traverses the response object and converts any ISO date strings into JavaScript Date objects.
 * @param body Response body
 * @returns void
 */
function handleDates(body: any) {
    if (body === null || body === undefined || typeof body !== 'object') return body;

    for (const key of Object.keys(body)) {
        const value = body[key];

        if (value && typeof value === 'string' && isoDateFormat.test(value)) {
            body[key] = new Date(value);
        } else if (Array.isArray(value) && value.length === 2 && value[0] instanceof Date && typeof value[1] === 'number') {
            body[key] = value[0];
        } else if (typeof value === 'object') {
            handleDates(value);
        }
    }
}

/**
 * Extracts filename from Content-Disposition header
 * @param disposition Content-Disposition header value
 * @returns Extracted filename or null if not found
 */
function getFilename(disposition: string): string | null {
    if (!disposition) return null;

    let filename = null;

    // 1. Try to find the UTF-8 version (filename*)
    const filenameStarMatch = disposition.match(/filename\*=(?:UTF-8'')([^;]+)/i);
    if (filenameStarMatch && filenameStarMatch[1]) {
        try {
            // Percent-decode the UTF-8 string
            filename = decodeURIComponent(filenameStarMatch[1]);
        } catch (e) {
            console.error('Failed to decode filename*', e);
        }
    }

    // 2. Fallback to standard filename if filename* was not found or failed
    if (!filename) {
        const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
        }
    }

    return filename;
}

function nullify(o: Record<string, any>) {
    for (const k in o) {
        if (o[k] === '') o[k] = null;
        if (typeof o[k] === 'object') nullify(o[k]);
    }
}
